/**
 * Utilities for creating buffer polygons around routes for visualization
 */

import type { GeoJSON } from 'geojson'

/**
 * Convert miles to meters
 */
function milesToMeters(miles: number): number {
  return miles * 1609.34
}

/**
 * Calculate bbox from a point and radius (matches backend calculatePointBufferBbox).
 * Used for live dead-end search on map click.
 */
export function calculatePointBufferBbox(
  longitude: number,
  latitude: number,
  radiusMiles: number,
  multiplier: number = 1.0
): [number, number, number, number] {
  const radiusMeters = milesToMeters(radiusMiles) * multiplier
  const metersPerDegreeLat = 111320
  const metersPerDegreeLon = 111320 * Math.cos((latitude * Math.PI) / 180)
  const latDelta = radiusMeters / metersPerDegreeLat
  const lonDelta = radiusMeters / metersPerDegreeLon
  return [
    Math.max(-180, longitude - lonDelta),
    Math.max(-90, latitude - latDelta),
    Math.min(180, longitude + lonDelta),
    Math.min(90, latitude + latDelta)
  ]
}

/**
 * Calculate approximate degrees per meter at a given latitude
 */
function getDegreesPerMeter(latitude: number): { lat: number; lon: number } {
  const metersPerDegreeLat = 111320 // Approximately constant
  const metersPerDegreeLon = 111320 * Math.cos(latitude * Math.PI / 180)
  return {
    lat: 1 / metersPerDegreeLat,
    lon: 1 / metersPerDegreeLon
  }
}

/**
 * Create a simple buffer polygon around a route
 * Uses a simplified approach: creates offset points perpendicular to the route
 * 
 * @param routeGeometry Route geometry (LineString or MultiLineString)
 * @param radiusMiles Radius in miles
 * @returns Polygon geometry representing the buffer
 */
export function createRouteBufferPolygon(
  routeGeometry: GeoJSON.LineString | GeoJSON.MultiLineString,
  radiusMiles: number
): GeoJSON.Polygon | null {
  if (!routeGeometry) {
    return null
  }

  // Extract coordinates
  let coordinates: [number, number][] = []
  if (routeGeometry.type === 'LineString') {
    coordinates = routeGeometry.coordinates as [number, number][]
  } else if (routeGeometry.type === 'MultiLineString') {
    coordinates = (routeGeometry.coordinates as [number, number][][]).flat()
  } else {
    return null
  }

  if (coordinates.length < 2) {
    return null
  }

  const radiusMeters = milesToMeters(radiusMiles)
  
  // Calculate average latitude for degree conversion
  const avgLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length
  const degreesPerMeter = getDegreesPerMeter(avgLat)
  
  const latDelta = radiusMeters * degreesPerMeter.lat
  const lonDelta = radiusMeters * degreesPerMeter.lon

  // Create buffer points on both sides of the route
  const leftPoints: [number, number][] = []
  const rightPoints: [number, number][] = []

  for (let i = 0; i < coordinates.length; i++) {
    const [lng, lat] = coordinates[i]
    
    if (i === 0) {
      // First point: use direction to next point
      const [nextLng, nextLat] = coordinates[i + 1]
      const dx = nextLng - lng
      const dy = nextLat - lat
      const length = Math.sqrt(dx * dx + dy * dy)
      
      if (length > 0) {
        // Perpendicular vector (rotate 90 degrees)
        const perpX = -dy / length
        const perpY = dx / length
        
        leftPoints.push([
          lng + perpX * lonDelta,
          lat + perpY * latDelta
        ])
        rightPoints.push([
          lng - perpX * lonDelta,
          lat - perpY * latDelta
        ])
      } else {
        // Fallback: use simple offset
        leftPoints.push([lng - lonDelta, lat])
        rightPoints.push([lng + lonDelta, lat])
      }
    } else if (i === coordinates.length - 1) {
      // Last point: use direction from previous point
      const [prevLng, prevLat] = coordinates[i - 1]
      const dx = lng - prevLng
      const dy = lat - prevLat
      const length = Math.sqrt(dx * dx + dy * dy)
      
      if (length > 0) {
        const perpX = -dy / length
        const perpY = dx / length
        
        leftPoints.push([
          lng + perpX * lonDelta,
          lat + perpY * latDelta
        ])
        rightPoints.push([
          lng - perpX * lonDelta,
          lat - perpY * latDelta
        ])
      } else {
        leftPoints.push([lng - lonDelta, lat])
        rightPoints.push([lng + lonDelta, lat])
      }
    } else {
      // Middle points: use average direction
      const [prevLng, prevLat] = coordinates[i - 1]
      const [nextLng, nextLat] = coordinates[i + 1]
      
      const dx1 = lng - prevLng
      const dy1 = lat - prevLat
      const dx2 = nextLng - lng
      const dy2 = nextLat - lat
      
      const avgDx = (dx1 + dx2) / 2
      const avgDy = (dy1 + dy2) / 2
      const length = Math.sqrt(avgDx * avgDx + avgDy * avgDy)
      
      if (length > 0) {
        const perpX = -avgDy / length
        const perpY = avgDx / length
        
        leftPoints.push([
          lng + perpX * lonDelta,
          lat + perpY * latDelta
        ])
        rightPoints.push([
          lng - perpX * lonDelta,
          lat - perpY * latDelta
        ])
      } else {
        leftPoints.push([lng - lonDelta, lat])
        rightPoints.push([lng + lonDelta, lat])
      }
    }
  }

  // Reverse right points and combine to form polygon
  const polygonCoordinates: [number, number][] = [
    ...leftPoints,
    ...rightPoints.reverse(),
    leftPoints[0] // Close the polygon
  ]

  return {
    type: 'Polygon',
    coordinates: [polygonCoordinates]
  }
}

